<script lang="ts" setup>
import DropDown from '@/components/common/DropDown.vue';
import CESGRoles from '@/components/common/roles/CESGRoles.vue';
import {
  PhArrowDown,
  PhArrowUp,
  PhFloppyDisk,
  PhMagnifyingGlass,
  PhTrash,
  PhUsers,
  PhX,
} from '@phosphor-icons/vue';
import { useI18n } from 'vue-i18n';
import {
  useDefaultReviewerSelectorLogic,
  type UseDefaultReviewerSelectorLogicProps,
} from './logic/useDefaultReviewerSelectorLogic';
import type { DefaultReviewerSelectorAction } from './types';

export type DefaultReviewerSelectorProps = UseDefaultReviewerSelectorLogicProps;
const props = defineProps<DefaultReviewerSelectorProps>();
const emit = defineEmits<{
  action: [payload: DefaultReviewerSelectorAction];
}>();
const { t } = useI18n();
const { state, action } = useDefaultReviewerSelectorLogic(props, emit);
</script>

<template>
  <button
    class="selector-trigger"
    :class="{ 'is-loading': state.isSaving }"
    :disabled="state.isSaving"
    @click="action.open()"
  >
    <span v-if="state.isSaving" class="spinner" />
    <PhUsers v-else :size="15" />
    <span>{{ t('invoices.defaultReviewerSelector.triggerLabel') }}</span>
    <span v-if="state.selectedReviewers.length > 0" class="trigger-count">{{
      state.selectedReviewers.length
    }}</span>
  </button>

  <Teleport to="body">
    <div class="action-sheet-overlay">
      <Transition name="sheet">
        <div v-if="state.isOpen" class="action-sheet">
          <div class="action-sheet-header">
            <span class="action-sheet-title">{{
              t('invoices.defaultReviewerSelector.title')
            }}</span>
            <button class="close-btn" @click="action.close()">
              <PhX :size="18" />
            </button>
          </div>

          <div class="add-reviewer-row" v-if="state.show.addReviewerRow">
            <DropDown
              :items="state.dropdownItems"
              :model-value="state.dropdownValue"
              :label="t('invoices.defaultReviewerSelector.addReviewer')"
              @update:model-value="action.addReviewer($event as string)"
            />
          </div>

          <div class="search-row">
            <PhMagnifyingGlass :size="15" class="search-icon" />
            <input
              class="search-input"
              type="text"
              :placeholder="
                t('invoices.defaultReviewerSelector.searchPlaceholder')
              "
              :value="state.query"
              @input="action.search(($event.target as HTMLInputElement).value)"
            />
          </div>

          <ul class="reviewer-list">
            <li
              v-for="(reviewer, index) in state.reviewers"
              :key="reviewer.id"
              class="reviewer-item"
              :class="{ 'reviewer-item--ineligible': !reviewer.isEligible }"
            >
              <div class="reviewer-order-btns">
                <button
                  v-if="state.show.orderButtons"
                  class="order-btn"
                  :disabled="index === 0"
                  @click.stop="action.moveUp(reviewer.id)"
                >
                  <PhArrowUp :size="13" />
                </button>
                <span class="reviewer-position">{{ index + 1 }}</span>
                <button
                  v-if="state.show.orderButtons"
                  class="order-btn"
                  :disabled="index === state.reviewers.length - 1"
                  @click.stop="action.moveDown(reviewer.id)"
                >
                  <PhArrowDown :size="13" />
                </button>
              </div>
              <div class="reviewer-info">
                <div class="reviewer-name-row">
                  <span class="reviewer-name">{{ reviewer.name }}</span>
                  <CESGRoles
                    :data="{ role: reviewer.role }"
                    class="reviewer-role-pill"
                  />
                </div>
                <a
                  class="reviewer-email"
                  :href="`mailto:${reviewer.email}`"
                  @click.stop
                  >{{ reviewer.email }}</a
                >
                <span v-if="!reviewer.isEligible" class="reviewer-ineligible-pill">
                  {{ t('invoices.defaultReviewerSelector.noReviewPermission') }}
                </span>
              </div>
              <button
                v-if="state.show.removeButton"
                class="remove-btn"
                @click.stop="action.deselect(reviewer.id)"
              >
                <PhTrash :size="14" />
              </button>
            </li>
            <li v-if="state.reviewers.length === 0" class="empty-item">
              {{ t('invoices.defaultReviewerSelector.empty') }}
            </li>
          </ul>

          <div class="action-sheet-footer" v-if="state.show.footer">
            <button
              class="footer-btn footer-btn--cancel"
              @click="action.cancel()"
            >
              {{ t('invoices.defaultReviewerSelector.cancel') }}
            </button>
            <button
              class="footer-btn footer-btn--save"
              :disabled="!state.canSave"
              @click="action.save()"
            >
              <span v-if="state.isSaving" class="spinner spinner--light" />
              <PhFloppyDisk v-else :size="15" />
              {{
                state.isSaving
                  ? t('invoices.defaultReviewerSelector.saving')
                  : t('invoices.defaultReviewerSelector.save')
              }}
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<style scoped>
.selector-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.75rem 1.25rem;
  border: 1px solid #d0d0d8;
  border-radius: 9999px;
  background: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  color: #444;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
}

.selector-trigger:hover {
  background: #f5f5f7;
  border-color: #b0b0bc;
}

.selector-trigger.is-loading {
  opacity: 0.6;
  cursor: wait;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #d0d0d8;
  border-top-color: #444;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
}

.spinner--light {
  border-color: rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.trigger-count {
  font-size: 0.875rem;
  font-weight: 600;
  color: #444;
}

.action-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  pointer-events: none;
}

.action-sheet {
  position: fixed;
  top: 1.5rem;
  left: 0;
  bottom: 0;
  width: 360px;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  border-radius: 0 12px 12px 0;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.1);
  pointer-events: all;
}

.action-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.action-sheet-title {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  padding: 0.25rem;
}

.close-btn:hover {
  color: #222;
}

.add-reviewer-row {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.search-icon {
  color: #aaa;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: 0.875rem;
  color: #222;
  background: transparent;
}

.search-input::placeholder {
  color: #bbb;
}

.reviewer-list {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
}

.reviewer-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.1s;
}

.reviewer-order-btns {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex-shrink: 0;
}

.order-btn {
  background: none;
  border: none;
  padding: 0.1rem;
  cursor: pointer;
  color: #aaa;
  display: flex;
  align-items: center;
  border-radius: 3px;
  transition: color 0.1s;
}

.order-btn:hover:not(:disabled) {
  color: #444;
}

.order-btn:disabled {
  opacity: 0.25;
  cursor: default;
}

.reviewer-position {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--primary-color);
  text-align: center;
  line-height: 1;
}

.reviewer-item:last-child {
  border-bottom: none;
}

.reviewer-item:hover {
  background: #f8f8fc;
}

.remove-btn {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #bbb;
  display: flex;
  align-items: center;
  border-radius: 3px;
  flex-shrink: 0;
  transition: color 0.1s;
}

.remove-btn:hover {
  color: #e53e3e;
}

.reviewer-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
  min-width: 0;
}

.reviewer-name-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.reviewer-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #222;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.reviewer-role-pill {
  display: inline-block;
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--primary-color);
  background: color-mix(in srgb, var(--primary-color) 10%, white);
  border: 1px solid color-mix(in srgb, var(--primary-color) 25%, white);
  border-radius: 9999px;
  padding: 0.1rem 0.5rem;
  line-height: 1.4;
  margin-left: auto;
}

.reviewer-email {
  font-size: 0.78rem;
  color: #888;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  text-decoration: none;
}

.reviewer-email:hover {
  color: var(--primary-color);
  text-decoration: underline;
}

.reviewer-item--ineligible .reviewer-name,
.reviewer-item--ineligible .reviewer-email {
  color: #aaa;
}

.reviewer-ineligible-pill {
  display: inline-block;
  align-self: flex-start;
  margin-top: 0.2rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #b7791f;
  background: #fdf6e3;
  border: 1px solid #f0d98c;
  border-radius: 9999px;
  padding: 0.1rem 0.5rem;
  line-height: 1.4;
}

.empty-item {
  padding: 1.5rem 1rem;
  text-align: center;
  color: #aaa;
  font-size: 0.875rem;
}

.action-sheet-footer {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.footer-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.55rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
}

.footer-btn--cancel {
  background: #fff;
  border: 1px solid #d0d0d8;
  color: #444;
}

.footer-btn--cancel:hover {
  background: #f5f5f7;
  border-color: #b0b0bc;
}

.footer-btn--save {
  background: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: #fff;
}

.footer-btn--save:hover:not(:disabled) {
  filter: brightness(1.08);
}

.footer-btn--save:disabled {
  background: #e0e0e8;
  border-color: #e0e0e8;
  color: #aaa;
  cursor: not-allowed;
}

.sheet-enter-active,
.sheet-leave-active {
  transition: transform 0.25s ease;
}

.sheet-enter-from,
.sheet-leave-to {
  transform: translateX(-100%);
}
</style>
